import React, { useState, useEffect } from 'react';
import { useNotifications } from '@byscott-io/corebyscott';
import { meals, mealRecipes, recipes, recipeCategories } from '../api/client';
import Modal from '../components/common/Modal';
import CategoryFilter from '../components/common/CategoryFilter';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'dessert'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function MealPlannerPage() {
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [mealsByDate, setMealsByDate] = useState({});
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showMealModal, setShowMealModal] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [newMealType, setNewMealType] = useState('');
  const [newMealNotes, setNewMealNotes] = useState('');
  const notifications = useNotifications();

  useEffect(() => {
    loadCalendar();
  }, [currentWeekStart]);

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  function formatDate(date) {
    return date.toISOString().split('T')[0];
  }

  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const endDate = addDays(currentWeekStart, 6);
      const response = await meals.calendar(formatDate(currentWeekStart), formatDate(endDate));
      setMealsByDate(response.data.meals_by_date || {});
    } catch {
      notifications.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      const [recipesRes, categoriesRes] = await Promise.all([
        recipes.list(),
        recipeCategories.list(),
      ]);
      setAvailableRecipes(recipesRes.data.recipes || []);
      setCategories(categoriesRes.data.categories || []);
    } catch {
      notifications.error('Failed to load recipes');
    }
  };

  // Filter recipes by selected categories and search
  const filteredRecipes = availableRecipes.filter((recipe) => {
    // Filter by search text
    if (recipeSearch.trim() && !recipe.title.toLowerCase().includes(recipeSearch.toLowerCase())) {
      return false;
    }
    // Filter by categories
    if (selectedCategories.length > 0) {
      const recipeCatIds = recipe.categories?.map((c) => c.id) || [];
      if (!selectedCategories.some((id) => recipeCatIds.includes(id))) {
        return false;
      }
    }
    return true;
  });

  const handlePrevWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const handleToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setSelectedMeal(null);
    setNewMealType('');
    setNewMealNotes('');
    setShowMealModal(true);
  };

  const handleCreateMeal = async () => {
    try {
      const response = await meals.create({
        meal: {
          meal_date: formatDate(selectedDate),
          meal_type: newMealType || null,
          notes: newMealNotes || null
        }
      });
      const dateKey = formatDate(selectedDate);
      setMealsByDate(prev => ({
        ...prev,
        [dateKey]: [...(prev[dateKey] || []), response.data.meal]
      }));
      setSelectedMeal(response.data.meal);
      setShowMealModal(false);
      await loadRecipes();
      setShowRecipeModal(true);
      notifications.success('Meal created');
    } catch {
      notifications.error('Failed to create meal');
    }
  };

  const handleMealClick = async (meal) => {
    setSelectedMeal(meal);
    await loadRecipes();
    setShowRecipeModal(true);
  };

  const handleAddRecipeToMeal = async (recipeId) => {
    try {
      await mealRecipes.add(selectedMeal.id, recipeId);
      loadCalendar();
      notifications.success('Recipe added to meal');
    } catch {
      notifications.error('Failed to add recipe');
    }
  };

  const handleFeelingLucky = async () => {
    try {
      const response = await recipes.feelingLucky();
      if (response.data.recipe) {
        await handleAddRecipeToMeal(response.data.recipe.id);
      } else {
        notifications.info(response.data.message || 'No recipes found');
      }
    } catch {
      notifications.error('Failed to find a recipe');
    }
  };

  const handleRemoveRecipeFromMeal = async (mealRecipeId) => {
    try {
      await mealRecipes.remove(selectedMeal.id, mealRecipeId);
      loadCalendar();
    } catch {
      notifications.error('Failed to remove recipe');
    }
  };

  const handleDeleteMeal = async (mealId, dateKey) => {
    if (!confirm('Delete this meal?')) return;
    try {
      await meals.delete(mealId);
      setMealsByDate(prev => ({
        ...prev,
        [dateKey]: (prev[dateKey] || []).filter(m => m.id !== mealId)
      }));
      setShowRecipeModal(false);
      notifications.success('Meal deleted');
    } catch {
      notifications.error('Failed to delete meal');
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Meal Planner</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevWeek}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            ←
          </button>
          <button
            onClick={handleToday}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Today
          </button>
          <button
            onClick={handleNextWeek}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            →
          </button>
        </div>
      </div>

      {/* Week Display */}
      <div className="text-center text-gray-600 dark:text-gray-400">
        {currentWeekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} -{' '}
        {addDays(currentWeekStart, 6).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {/* Day Headers */}
          {DAYS.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2"
            >
              {day}
            </div>
          ))}

          {/* Day Cells */}
          {weekDays.map((date) => {
            const dateKey = formatDate(date);
            const dayMeals = mealsByDate[dateKey] || [];
            const today = isToday(date);

            return (
              <div
                key={dateKey}
                onClick={() => handleDayClick(date)}
                className={`min-h-32 p-2 rounded-lg cursor-pointer transition-colors ${
                  today
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-500'
                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className={`text-sm font-medium mb-2 ${today ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-white'}`}>
                  {date.getDate()}
                </div>
                <div className="space-y-1">
                  {dayMeals.map((meal) => (
                    <div
                      key={meal.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMealClick(meal);
                      }}
                      className="p-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded truncate hover:bg-green-200 dark:hover:bg-green-900/50"
                    >
                      {meal.meal_type && <span className="font-medium">{meal.meal_type}: </span>}
                      {meal.recipes?.map(r => r.recipe_title).join(', ') || meal.notes || 'No recipes'}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Meal Modal */}
      <Modal isOpen={showMealModal} onClose={() => setShowMealModal(false)} title="Add Meal">
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Meal Type (optional)
            </label>
            <select
              value={newMealType}
              onChange={(e) => setNewMealType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">No specific type</option>
              {MEAL_TYPES.map((type) => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              placeholder="e.g., with side salad, extra garlic bread"
              value={newMealNotes}
              onChange={(e) => setNewMealNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowMealModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateMeal}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Create & Add Recipes
            </button>
          </div>
        </div>
      </Modal>

      {/* Recipe Selection Modal */}
      <Modal isOpen={showRecipeModal} onClose={() => setShowRecipeModal(false)} title="Meal Recipes" size="lg">
        {selectedMeal && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedMeal.meal_type && <span className="font-medium capitalize">{selectedMeal.meal_type} - </span>}
                  {selectedMeal.meal_date}
                </p>
                {selectedMeal.notes && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 italic">"{selectedMeal.notes}"</p>
                )}
              </div>
              <button
                onClick={() => handleDeleteMeal(selectedMeal.id, selectedMeal.meal_date)}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Delete Meal
              </button>
            </div>

            {/* Current Recipes */}
            {selectedMeal.recipes?.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Current Recipes:</h4>
                <div className="space-y-2">
                  {selectedMeal.recipes.map((mr) => (
                    <div
                      key={mr.id}
                      className="flex justify-between items-center p-2 bg-gray-100 dark:bg-gray-700 rounded"
                    >
                      <span className="text-gray-900 dark:text-white">{mr.recipe_title}</span>
                      <button
                        onClick={() => handleRemoveRecipeFromMeal(mr.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Recipe */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 dark:text-white">Add Recipe:</h4>
                <button
                  onClick={handleFeelingLucky}
                  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                  title="Add a random recipe you haven't made recently"
                >
                  🎲 Feeling Lucky
                </button>
              </div>

              {/* Search and Filter */}
              <div className="space-y-2 mb-3">
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={recipeSearch}
                  onChange={(e) => setRecipeSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
                {categories.length > 0 && (
                  <CategoryFilter
                    categories={categories}
                    selected={selectedCategories}
                    onChange={setSelectedCategories}
                  />
                )}
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredRecipes
                  .filter(r => !selectedMeal.recipes?.some(mr => mr.recipe_id === r.id))
                  .map((recipe) => (
                    <button
                      key={recipe.id}
                      onClick={() => handleAddRecipeToMeal(recipe.id)}
                      className="w-full p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{recipe.title}</div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        {recipe.total_time_minutes > 0 && (
                          <span>{recipe.total_time_minutes} min</span>
                        )}
                        {recipe.categories?.slice(0, 2).map((cat) => (
                          <span
                            key={cat.id}
                            className="px-1.5 py-0.5 text-xs rounded"
                            style={{ backgroundColor: cat.color + '20', color: cat.color }}
                          >
                            {cat.name}
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                {filteredRecipes.filter(r => !selectedMeal.recipes?.some(mr => mr.recipe_id === r.id)).length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No recipes match your filters
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
